import { FC, useEffect, useMemo } from 'react';
import { Grid, Typography, Divider, CircularProgress, Box } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import StructuredModal from '../../../../components/structuredModal/structuredModal';
import ControlledTextField from '../../../../components/controlledComponents/ControlledTextField.component';
import ControlledSelectField from '../../../../components/controlledComponents/ControlledSelectField.component';
import { useUsers } from '../../contexts/users.context';
import { useCreateInternalUser } from '../../hooks/useInternalUserHooks';
import { useStaffEligibleRoles } from '../../hooks/useStaffEligibleRolesHook';
import { TKeyValue } from 'src/utils/commonInterfaces';
import { zOptionalDateStringFromInput } from 'src/utils/zodFormHelpers';
import { getUserContext } from 'src/views/modules/staff/subviews/staffList/staffManageDialog.view';
import { UserStatus } from 'src/apis/users/users.api.interfaces';

const internalUserFormSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  middleName: z.string().optional(),
  lastName: z.string().min(1, 'Last name is required'),
  gender: z.string().min(1, 'Gender is required'),
  dateOfBirth: zOptionalDateStringFromInput({ message: 'Please enter a valid date (YYYY-MM-DD)' }),
  userName: z.string().min(3, 'Username must be at least 3 characters'),
  userPassword: z.string().min(6, 'Password must be at least 6 characters'),
  userRole: z.string().min(1, 'Role is required'),
  email: z.string().email('Valid email is required'),
  phoneNumber: z.string().min(10, 'Phone number is required'),
  countryCode: z.string().optional(),
  status: z.nativeEnum(UserStatus),
  street1: z.string().min(1, 'Street is required'),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  pinCode: z.string().min(1, 'Pin code is required'),
  country: z.string().min(1, 'Country is required'),
});

type InternalUserFormData = z.infer<typeof internalUserFormSchema>;

const AddEditInternalUserView: FC = () => {
  const {
    state: { isAddEditInternalOpen, isLoading },
    closeAddEditInternal,
  } = useUsers();

  const createMutation = useCreateInternalUser();
  const { data: eligibleRoles, isLoading: rolesLoading } = useStaffEligibleRoles(isAddEditInternalOpen);

  const roleOptions: TKeyValue<string>[] = useMemo(() => {
    if (!eligibleRoles) return [];
    return eligibleRoles.map((role) => ({
      key: role.name,
      value: role.description || role.name,
    }));
  }, [eligibleRoles]);

  const genderOptions: TKeyValue<string>[] = [
    { key: 'male', value: 'Male' },
    { key: 'female', value: 'Female' },
    { key: 'trans', value: 'Other' },
  ];

  const statusOptions: TKeyValue<string>[] = [
    { key: UserStatus.ACTIVE, value: 'Active' },
    { key: UserStatus.INACTIVE, value: 'Inactive' },
    { key: UserStatus.PENDING, value: 'Pending' },
    { key: UserStatus.BLOCKED, value: 'Blocked' },
  ];

  const defaultValues: InternalUserFormData = {
    firstName: '',
    middleName: '',
    lastName: '',
    gender: '',
    dateOfBirth: '',
    userName: '',
    userPassword: '',
    userRole: '',
    email: '',
    phoneNumber: '',
    countryCode: '+91',
    status: UserStatus.ACTIVE,
    street1: '',
    city: '',
    state: '',
    pinCode: '',
    country: 'India',
  };

  const methods = useForm<InternalUserFormData>({
    resolver: zodResolver(internalUserFormSchema),
    mode: 'all',
    defaultValues,
  });

  const {
    control: formControl,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = methods;
  const control = formControl as any;
  const e = errors as any;

  useEffect(() => {
    if (isAddEditInternalOpen) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddEditInternalOpen, reset]);

  const onSubmitForm = async (data: InternalUserFormData) => {
    const { instituteId, userId: loggedInUserId } = getUserContext();

    const payload = {
      firstName: data.firstName,
      middleName: data.middleName,
      lastName: data.lastName,
      gender: data.gender,
      dateOfBirth: data.dateOfBirth,
      userName: data.userName,
      userPassword: data.userPassword,
      userRole: data.userRole,
      status: data.status,
      userCategory: 'staff',
      instituteId,
      createdBy: loggedInUserId,
      address: {
        street1: data.street1,
        city: data.city,
        state: data.state,
        pinCode: data.pinCode,
        country: data.country,
      },
      contact: {
        countryCode: data.countryCode || '+91',
        phoneNumber: data.phoneNumber,
        email: data.email,
      },
    };

    try {
      await createMutation.mutateAsync(payload);
      closeAddEditInternal();
    } catch (error) {
      console.error('Error creating internal user:', error);
    }
  };

  return (
    <StructuredModal
      isOpen={isAddEditInternalOpen}
      onClose={closeAddEditInternal}
      title="Add Internal User"
      drawerWidth="60vw"
      footerPositiveAction={handleSubmit(onSubmitForm)}
      footerPositiveActionText="Save"
      footerNegativeAction={closeAddEditInternal}
      footerNegativeActionText="Cancel"
      isLoading={isLoading || createMutation.isPending}
      footerPositiveActionDisabled={!isValid}
    >
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmitForm)}
          style={{ width: '100%' }}
          data-testid="internal-user-form"
        >
          {/* Basic Information */}
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            Basic Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <ControlledTextField
                name="firstName"
                id="firstName"
                control={control}
                label="First Name"
                required
                errorMessage={e.firstName?.message}
                isGridItem={false}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <ControlledTextField
                name="middleName"
                id="middleName"
                control={control}
                label="Middle Name"
                isGridItem={false}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <ControlledTextField
                name="lastName"
                id="lastName"
                control={control}
                label="Last Name"
                required
                errorMessage={e.lastName?.message}
                isGridItem={false}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <ControlledSelectField
                name="gender"
                id="gender"
                control={control}
                label="Gender"
                itemList={genderOptions}
                isRequired
                errorMessage={e.gender?.message}
                isGridItem={false}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <ControlledTextField
                name="dateOfBirth"
                id="dateOfBirth"
                control={control}
                label="Date of Birth"
                type="date"
                errorMessage={e.dateOfBirth?.message}
                isGridItem={false}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              {rolesLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography variant="caption">Loading roles...</Typography>
                </Box>
              ) : (
                <ControlledSelectField
                  name="userRole"
                  id="userRole"
                  control={control}
                  label="Role"
                  itemList={roleOptions}
                  isRequired
                  errorMessage={e.userRole?.message}
                  isGridItem={false}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              <ControlledSelectField
                name="status"
                id="status"
                control={control}
                label="Status"
                itemList={statusOptions}
                isRequired
                errorMessage={e.status?.message}
                isGridItem={false}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Credentials */}
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            Login Credentials
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <ControlledTextField
                name="userName"
                id="userName"
                control={control}
                label="Username"
                required
                errorMessage={e.userName?.message}
                isGridItem={false}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ControlledTextField
                name="userPassword"
                id="userPassword"
                control={control}
                label="Password"
                required
                type="password"
                errorMessage={e.userPassword?.message}
                isGridItem={false}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Contact Information */}
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            Contact Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <ControlledTextField
                name="email"
                id="email"
                control={control}
                label="Email"
                required
                errorMessage={e.email?.message}
                isGridItem={false}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <ControlledTextField
                name="countryCode"
                id="countryCode"
                control={control}
                label="Code"
                isGridItem={false}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <ControlledTextField
                name="phoneNumber"
                id="phoneNumber"
                control={control}
                label="Phone Number"
                required
                errorMessage={e.phoneNumber?.message}
                isGridItem={false}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Address */}
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            Address
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <ControlledTextField
                name="street1"
                id="street1"
                control={control}
                label="Street"
                required
                errorMessage={e.street1?.message}
                isGridItem={false}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ControlledTextField
                name="city"
                id="city"
                control={control}
                label="City"
                required
                errorMessage={e.city?.message}
                isGridItem={false}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <ControlledTextField
                name="state"
                id="state"
                control={control}
                label="State"
                required
                errorMessage={e.state?.message}
                isGridItem={false}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <ControlledTextField
                name="pinCode"
                id="pinCode"
                control={control}
                label="Pin Code"
                required
                errorMessage={e.pinCode?.message}
                isGridItem={false}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <ControlledTextField
                name="country"
                id="country"
                control={control}
                label="Country"
                required
                errorMessage={e.country?.message}
                isGridItem={false}
              />
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </StructuredModal>
  );
};

export default AddEditInternalUserView;
